import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { 
  FiAward, 
  FiArrowLeft, 
  FiCheck, 
  FiCalendar, 
  FiMapPin, 
  FiImage, 
  FiDollarSign, 
  FiSliders, 
  FiFileText,
  FiLink,
  FiUploadCloud,
  FiZap
} from 'react-icons/fi';
import ContestService from '../services/ContestService';
import EventService from '../services/EventService';
import instance from '../services/AxiosServices';
import SearchableSelect from '../components/SearchableSelect';

const INDIAN_STATES_CITIES = {
  'Rajasthan': ['Bikaner', 'Jaipur', 'Jodhpur', 'Udaipur', 'Kota', 'Ajmer', 'Bhilwara', 'Alwar', 'Sikar', 'Churu', 'Hanumangarh', 'Sri Ganganagar'],
  'Punjab': ['Amritsar', 'Chandigarh', 'Ludhiana', 'Jalandhar', 'Patiala', 'Bathinda', 'Pathankot', 'Mohali', 'Hoshiarpur', 'Firozpur'],
  'Delhi NCR': ['New Delhi', 'Noida', 'Gurugram', 'Ghaziabad', 'Faridabad'],
  'Maharashtra': ['Mumbai', 'Pune', 'Nagpur', 'Thane', 'Nashik', 'Aurangabad', 'Solapur', 'Kolhapur', 'Amravati'],
  'Karnataka': ['Bengaluru', 'Mysuru', 'Hubballi', 'Mangaluru', 'Belagavi', 'Davangere', 'Ballari'],
  'Gujarat': ['Ahmedabad', 'Surat', 'Vadodara', 'Rajkot', 'Bhavnagar', 'Jamnagar', 'Gandhinagar', 'Junagadh'],
  'Telangana': ['Hyderabad', 'Warangal', 'Nizamabad', 'Karimnagar', 'Khammam'],
  'Tamil Nadu': ['Chennai', 'Coimbatore', 'Madurai', 'Tiruchirappalli', 'Salem', 'Tirunelveli'],
  'West Bengal': ['Kolkata', 'Howrah', 'Durgapur', 'Asansol', 'Siliguri', 'Kharagpur'],
  'Uttar Pradesh': ['Lucknow', 'Kanpur', 'Varanasi', 'Agra', 'Meerut', 'Prayagraj', 'Noida', 'Ghaziabad', 'Bareilly', 'Aligarh'],
  'Madhya Pradesh': ['Indore', 'Bhopal', 'Gwalior', 'Jabalpur', 'Ujjain', 'Sagar', 'Satna'],
  'Bihar': ['Patna', 'Gaya', 'Bhagalpur', 'Muzaffarpur', 'Purnia', 'Darbhanga'],
  'Haryana': ['Gurugram', 'Faridabad', 'Panipat', 'Ambala', 'Karnal', 'Hisar', 'Rohtak'],
  'Himachal Pradesh': ['Shimla', 'Manali', 'Dharamshala', 'Solan', 'Mandi'],
  'Uttarakhand': ['Dehradun', 'Haridwar', 'Rishikesh', 'Haldwani', 'Roorkee'],
  'Goa': ['Panaji', 'Margao', 'Vasco da Gama', 'Mapusa'],
  'Kerala': ['Thiruvananthapuram', 'Kochi', 'Kozhikode', 'Thrissur', 'Kollam']
};

const DEFAULT_CONTEST_TYPES = [
  { name: 'Photo Contest', slug: 'photo-contest' },
  { name: 'Reels Contest', slug: 'reels-contest' },
  { name: '1v1 Photo Battle', slug: '1v1-photo-battle' },
  { name: 'Singing & Music Contest', slug: 'music-contest' },
  { name: 'Dance Contest', slug: 'dance-contest' },
  { name: 'Fashion & Modeling Contest', slug: 'fashion-contest' }
];

const CreateContest = () => {
  const history = useHistory();
  const [loading, setLoading] = useState(false);
  const [uploadingPoster, setUploadingPoster] = useState(false);
  const [uploadingBanner, setUploadingBanner] = useState(false);
  const [uploadingFrame, setUploadingFrame] = useState(false);

  // Dynamic Options from DB (eventtypes collection)
  const [categories, setCategories] = useState(DEFAULT_CONTEST_TYPES);
  const [dbStatesMap, setDbStatesMap] = useState(INDIAN_STATES_CITIES);
  const [partnerEvents, setPartnerEvents] = useState([]);

  const [formData, setFormData] = useState({
    title: '',
    type: 'Photo Contest',
    categorySlug: 'photo-contest',
    visibility: 'Public', // 'Public' | 'Happy Hour' | 'Private'
    city: '',
    state: '',
    posterUrl: '',
    bannerUrl: '',
    frameUrl: '', // Photo Frame Overlay PNG URL
    startDate: '',
    votingStartDate: '',
    expiryDate: '',
    prizeonewords: '₹10,000 Cash Prize + Trophy',
    cashPrize: 10000,
    isPaid: false, // Free vs Paid contest toggle
    entryFee: 0, // Join price / Entry fee in INR
    about: '',
    rules: '',
    eventId: '',
    status: 'LIVE',
    isFrameAllow: true,
    isVotingAllow: true,
    isUploadAllow: true,
  });

  useEffect(() => {
    // Load Contest Types (eventtypes collection), Cities & Partner Events from DB
    const loadMasterData = async () => {
      try {
        const [typesRes, citiesRes, eventsRes] = await Promise.all([
          instance.get('/contests/types').catch(() => null),
          instance.get('/cities').catch(() => null),
          EventService.getAllPartnerEvents().catch(() => null)
        ]);

        // Process Contest Types / Categories from MongoDB 'eventtypes' collection
        const rawTypes = typesRes?.data?.types || typesRes?.data || typesRes;
        if (Array.isArray(rawTypes) && rawTypes.length > 0) {
          setCategories(rawTypes);
          const first = typeof rawTypes[0] === 'string' ? rawTypes[0] : (rawTypes[0].name || 'Photo Contest');
          const firstSlug = typeof rawTypes[0] === 'string' ? first.toLowerCase().replace(/[^a-z0-9]+/g, '-') : (rawTypes[0].slug || 'photo-contest');
          
          setFormData(prev => ({
            ...prev,
            type: prev.type || first,
            categorySlug: prev.categorySlug || firstSlug
          }));
        }

        // Process States and Cities from DB
        const rawCities = citiesRes?.data || citiesRes;
        if (rawCities?.statesMap && Object.keys(rawCities.statesMap).length > 0) {
          setDbStatesMap(prev => ({ ...INDIAN_STATES_CITIES, ...rawCities.statesMap }));
        } else if (Array.isArray(rawCities?.allCities)) {
          const map = {};
          rawCities.allCities.forEach(c => {
            const st = c.stateName || 'Other';
            if (!map[st]) map[st] = [];
            if (!map[st].includes(c.name)) map[st].push(c.name);
          });
          if (Object.keys(map).length > 0) {
            setDbStatesMap(prev => ({ ...INDIAN_STATES_CITIES, ...map }));
          }
        }

        // Process Partner Events from DB
        const evList = eventsRes?.events || eventsRes?.data || eventsRes || [];
        if (Array.isArray(evList)) {
          setPartnerEvents(evList);
        }
      } catch (err) {
        console.error('Error loading master data for create contest:', err);
      }
    };
    loadMasterData();
  }, []);

  const handleStateChange = (selectedState) => {
    setFormData(prev => ({
      ...prev,
      state: selectedState,
      city: ''
    }));
  };

  // Alphabetically Sorted States & Cities
  const availableStates = Object.keys(dbStatesMap).sort((a, b) => a.localeCompare(b));
  const availableCitiesForState = formData.state ? (dbStatesMap[formData.state] || []).sort((a, b) => a.localeCompare(b)) : [];

  const handleFileUpload = async (file, targetType) => {
    if (!file) return;

    const data = new FormData();
    data.append('media', file);

    if (targetType === 'poster') setUploadingPoster(true);
    if (targetType === 'banner') setUploadingBanner(true);
    if (targetType === 'frame') setUploadingFrame(true);

    try {
      const res = await instance.post('/media/partner/upload', data, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      const uploadedUrl = res?.url || res?.data?.url || res?.mediaUrl || res?.path;

      if (uploadedUrl) {
        if (targetType === 'poster') {
          setFormData(prev => ({ ...prev, posterUrl: uploadedUrl }));
        } else if (targetType === 'banner') {
          setFormData(prev => ({ ...prev, bannerUrl: uploadedUrl }));
        } else if (targetType === 'frame') {
          setFormData(prev => ({ ...prev, frameUrl: uploadedUrl }));
        }
        alert('File uploaded successfully!');
      }
    } catch (err) {
      console.error('Upload error:', err);
      alert('Failed to upload file.');
    } finally {
      if (targetType === 'poster') setUploadingPoster(false);
      if (targetType === 'banner') setUploadingBanner(false);
      if (targetType === 'frame') setUploadingFrame(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.title) {
      alert('Please enter contest title.');
      return;
    }

    setLoading(true);
    try {
      const feeVal = formData.isPaid ? (Number(formData.entryFee) || 0) : 0;
      await ContestService.createContest({
        ...formData,
        cashPrize: Number(formData.cashPrize) || 0,
        entryFee: feeVal,
        isPaid: formData.isPaid,
        entrySettings: {
          isPaid: formData.isPaid,
          feeAmount: feeVal,
        },
        votingSettings: {
          IsFrameAllow: formData.isFrameAllow,
          frameUrl: formData.isFrameAllow ? formData.frameUrl : '',
        },
        poster: { url: formData.posterUrl },
        banner: { url: formData.bannerUrl },
      });
      alert('Contest created successfully! Submitted for Admin Approval.');
      history.push('/app/contests');
    } catch (err) {
      console.error('Error creating contest:', err);
      const errMsg = err.response?.data?.message || err.message || 'Failed to create contest.';
      alert(`Error creating contest: ${errMsg}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-5xl mx-auto space-y-6 text-left pb-24">
      {/* Header */}
      <div className="flex items-center gap-4 bg-white border border-slate-200 p-6 rounded-2xl shadow-sm">
        <button
          onClick={() => history.goBack()}
          className="p-2.5 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-700 border border-slate-200 transition-colors"
        >
          <FiArrowLeft className="w-4 h-4" />
        </button>
        <div>
          <h1 className="text-2xl font-black text-slate-900 tracking-tight flex items-center gap-2">
            <FiAward className="text-purple-700" /> Create New Contest / Competition
          </h1>
          <p className="text-xs font-medium text-slate-500 mt-1">Configure contest details, prizes, frame overlay & join price</p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Section 1: Basic Information */}
        <div className="bg-white border border-slate-200 p-6 md:p-8 rounded-2xl space-y-6 shadow-sm">
          <div className="flex items-center gap-2 text-purple-900 font-bold text-sm border-b border-slate-200 pb-3">
            <FiAward className="w-5 h-5 text-purple-700" />
            <h2>1. Contest Basic Details & Access Type</h2>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-xs font-bold text-slate-700 mb-1.5">Contest Title *</label>
              <input
                type="text"
                required
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder="e.g. Bikaner Elite Photo Contest 2026"
                className="w-full bg-white border border-slate-300 rounded-xl p-3.5 text-xs text-slate-900 outline-none focus:border-purple-600 font-semibold"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Contest Category Dropdown */}
              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1.5">Contest Category / Type *</label>
                <select
                  value={formData.type}
                  onChange={(e) => {
                    const selectedVal = e.target.value;
                    const matchedObj = categories.find(c => (typeof c === 'string' ? c : c.name) === selectedVal);
                    const slug = typeof matchedObj === 'object' && matchedObj?.slug 
                      ? matchedObj.slug 
                      : selectedVal.toLowerCase().replace(/[^a-z0-9]+/g, '-');

                    setFormData({
                      ...formData,
                      type: selectedVal,
                      categorySlug: slug
                    });
                  }}
                  className="w-full bg-white border border-slate-300 rounded-xl p-3.5 text-xs text-slate-900 outline-none focus:border-purple-600 font-medium"
                >
                  {categories.map((c, idx) => {
                    const catName = typeof c === 'string' ? c : (c.name || 'Contest Category');
                    return (
                      <option key={c._id || idx} value={catName}>
                        {catName}
                      </option>
                    );
                  })}
                </select>
              </div>

              {/* Visibility Selection */}
              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1.5 flex items-center gap-1">
                  <FiZap className="text-amber-600" /> Contest Visibility & Access Type
                </label>
                <select
                  value={formData.visibility}
                  onChange={(e) => setFormData({ ...formData, visibility: e.target.value })}
                  className="w-full bg-white border border-slate-300 rounded-xl p-3.5 text-xs text-slate-900 outline-none focus:border-purple-600 font-medium"
                >
                  <option value="Public">🌐 Public (Standard Contest)</option>
                  <option value="Happy Hour">🔥 Public (Happy Hour Boosted Votes)</option>
                  <option value="Private">🔒 Private (Exclusive / Invite-Only)</option>
                </select>
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1.5 flex items-center justify-between">
                  <span className="flex items-center gap-1">
                    <FiLink className="text-purple-700" /> Attach to Partner Event
                  </span>
                  <span className="text-[10px] text-slate-400 font-normal">(Optional)</span>
                </label>
                <select
                  value={formData.eventId}
                  onChange={(e) => setFormData({ ...formData, eventId: e.target.value })}
                  className="w-full bg-white border border-slate-300 rounded-xl p-3.5 text-xs text-slate-900 outline-none focus:border-purple-600 font-medium"
                >
                  <option value="">-- None / Standalone Contest (Optional) --</option>
                  {partnerEvents.map((ev) => {
                    const eventName = ev.title || ev.basic?.title || ev.name || 'Untitled Event';
                    const eventType = ev.type || ev.eventType || 'Event';
                    return (
                      <option key={ev._id || ev.eventId} value={ev._id || ev.eventId}>
                        {eventName} ({eventType})
                      </option>
                    );
                  })}
                </select>
              </div>
            </div>

            {/* State & City Selectors */}
            <div className="p-5 rounded-2xl bg-slate-50 border border-slate-200 space-y-4">
              <h3 className="text-xs font-bold text-emerald-800 flex items-center gap-1.5 uppercase tracking-wider">
                <FiMapPin className="w-4 h-4 text-emerald-600" /> State & Searchable City Selector
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold text-slate-700 mb-1.5">1. Select State *</label>
                  <SearchableSelect
                    options={availableStates}
                    value={formData.state}
                    onChange={handleStateChange}
                    placeholder="Search & Select State..."
                    searchPlaceholder="Type state name..."
                  />
                </div>

                <div>
                  <label className="block text-xs font-bold text-slate-700 mb-1.5">2. Select City *</label>
                  <SearchableSelect
                    options={availableCitiesForState}
                    value={formData.city}
                    onChange={(selectedCity) => setFormData({ ...formData, city: selectedCity })}
                    placeholder={formData.state ? `Search city in ${formData.state}...` : 'First select a State'}
                    searchPlaceholder={`Type city name...`}
                    disabled={!formData.state}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Section 2: Media Assets & Photo Frame */}
        <div className="bg-white border border-slate-200 p-6 md:p-8 rounded-2xl space-y-6 shadow-sm">
          <div className="flex items-center gap-2 text-purple-900 font-bold text-sm border-b border-slate-200 pb-3">
            <FiImage className="w-5 h-5 text-purple-700" />
            <h2>2. Media Assets & Custom Photo Frame Overlay</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-3 p-4 rounded-xl bg-slate-50 border border-slate-200">
              <label className="block text-xs font-bold text-slate-700">Poster Card Image (Vertical)</label>
              
              <div className="flex items-center gap-2">
                <input
                  type="url"
                  value={formData.posterUrl}
                  onChange={(e) => setFormData({ ...formData, posterUrl: e.target.value })}
                  placeholder="https://..."
                  className="flex-1 bg-white border border-slate-300 rounded-xl p-3 text-xs text-slate-900 outline-none focus:border-purple-600 font-medium"
                />
                
                <label className="px-3.5 py-3 rounded-xl bg-purple-700 hover:bg-purple-800 text-white text-xs font-bold cursor-pointer transition-all flex items-center gap-1.5 shrink-0 shadow-sm">
                  <FiUploadCloud className="w-4 h-4" />
                  <span>{uploadingPoster ? 'Uploading...' : 'Upload'}</span>
                  <input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    disabled={uploadingPoster}
                    onChange={(e) => handleFileUpload(e.target.files[0], 'poster')}
                  />
                </label>
              </div>

              {formData.posterUrl && (
                <div className="w-20 h-24 rounded-xl overflow-hidden border border-purple-200 bg-white mt-2">
                  <img src={formData.posterUrl} alt="Poster preview" className="w-full h-full object-cover" />
                </div>
              )}
            </div>

            <div className="space-y-3 p-4 rounded-xl bg-slate-50 border border-slate-200">
              <label className="block text-xs font-bold text-slate-700">Banner Cover Image (Landscape)</label>
              
              <div className="flex items-center gap-2">
                <input
                  type="url"
                  value={formData.bannerUrl}
                  onChange={(e) => setFormData({ ...formData, bannerUrl: e.target.value })}
                  placeholder="https://..."
                  className="flex-1 bg-white border border-slate-300 rounded-xl p-3 text-xs text-slate-900 outline-none focus:border-purple-600 font-medium"
                />

                <label className="px-3.5 py-3 rounded-xl bg-indigo-700 hover:bg-indigo-800 text-white text-xs font-bold cursor-pointer transition-all flex items-center gap-1.5 shrink-0 shadow-sm">
                  <FiUploadCloud className="w-4 h-4" />
                  <span>{uploadingBanner ? 'Uploading...' : 'Upload'}</span>
                  <input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    disabled={uploadingBanner}
                    onChange={(e) => handleFileUpload(e.target.files[0], 'banner')}
                  />
                </label>
              </div>

              {formData.bannerUrl && (
                <div className="w-36 h-20 rounded-xl overflow-hidden border border-indigo-200 bg-white mt-2">
                  <img src={formData.bannerUrl} alt="Banner preview" className="w-full h-full object-cover" />
                </div>
              )}
            </div>
          </div>

          {/* Photo Frame Overlay Upload Box (Rendered when isFrameAllow is true) */}
          {formData.isFrameAllow && (
            <div className="p-5 rounded-2xl bg-purple-50/60 border border-purple-200 space-y-3 animate-fade-in">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-xs font-bold text-purple-900 flex items-center gap-1.5 uppercase tracking-wider">
                    🖼️ Custom Photo Frame Overlay (PNG Image)
                  </h3>
                  <p className="text-[11px] text-slate-500 font-medium mt-0.5">
                    Upload a transparent PNG frame. Participant photos will be displayed inside this custom frame!
                  </p>
                </div>
                <span className="px-2.5 py-1 rounded-full text-[10px] font-extrabold bg-purple-200 text-purple-800">
                  Frame Enabled
                </span>
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="url"
                  value={formData.frameUrl}
                  onChange={(e) => setFormData({ ...formData, frameUrl: e.target.value })}
                  placeholder="https://.../contest-frame.png (Transparent PNG)"
                  className="flex-1 bg-white border border-purple-300 rounded-xl p-3 text-xs text-slate-900 outline-none focus:border-purple-600 font-medium"
                />

                <label className="px-4 py-3 rounded-xl bg-purple-700 hover:bg-purple-800 text-white text-xs font-bold cursor-pointer transition-all flex items-center gap-1.5 shrink-0 shadow-sm">
                  <FiUploadCloud className="w-4 h-4" />
                  <span>{uploadingFrame ? 'Uploading Frame...' : 'Upload Frame'}</span>
                  <input
                    type="file"
                    accept="image/png,image/*"
                    className="hidden"
                    disabled={uploadingFrame}
                    onChange={(e) => handleFileUpload(e.target.files[0], 'frame')}
                  />
                </label>
              </div>

              {formData.frameUrl && (
                <div className="flex items-center gap-3 mt-2 p-2 bg-white rounded-xl border border-purple-200 w-fit">
                  <div className="w-16 h-16 rounded-lg overflow-hidden border border-purple-300 bg-slate-100 flex items-center justify-center p-1">
                    <img src={formData.frameUrl} alt="Frame Overlay Preview" className="w-full h-full object-contain" />
                  </div>
                  <div>
                    <span className="text-xs font-bold text-purple-900 block">Frame Preview Loaded</span>
                    <span className="text-[10px] text-slate-500 font-medium">Ready to overlay on participant entries</span>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Section 3: Contest Join Pricing (Free vs Paid Contest) */}
        <div className="bg-white border border-slate-200 p-6 md:p-8 rounded-2xl space-y-6 shadow-sm">
          <div className="flex items-center gap-2 text-emerald-900 font-bold text-sm border-b border-slate-200 pb-3">
            <FiDollarSign className="w-5 h-5 text-emerald-600" />
            <h2>3. Contest Entry Fee & Pricing (Free vs Paid Contest)</h2>
          </div>

          <div className="space-y-4">
            <label className="block text-xs font-bold text-slate-700">Contest Participation Pricing Model *</label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <label
                onClick={() => setFormData({ ...formData, isPaid: false, entryFee: 0 })}
                className={`p-4 rounded-2xl border-2 cursor-pointer transition-all flex items-center gap-3 ${
                  !formData.isPaid
                    ? 'border-emerald-600 bg-emerald-50/60 shadow-xs'
                    : 'border-slate-200 bg-slate-50 hover:bg-slate-100'
                }`}
              >
                <input
                  type="radio"
                  name="pricingModel"
                  checked={!formData.isPaid}
                  onChange={() => setFormData({ ...formData, isPaid: false, entryFee: 0 })}
                  className="w-4 h-4 accent-emerald-600"
                />
                <div>
                  <span className="text-xs font-black text-slate-900 block">🟢 Free Entry Contest</span>
                  <span className="text-[11px] text-slate-500 font-medium">Anyone can join and upload photos for free</span>
                </div>
              </label>

              <label
                onClick={() => setFormData({ ...formData, isPaid: true })}
                className={`p-4 rounded-2xl border-2 cursor-pointer transition-all flex items-center gap-3 ${
                  formData.isPaid
                    ? 'border-purple-600 bg-purple-50/60 shadow-xs'
                    : 'border-slate-200 bg-slate-50 hover:bg-slate-100'
                }`}
              >
                <input
                  type="radio"
                  name="pricingModel"
                  checked={formData.isPaid}
                  onChange={() => setFormData({ ...formData, isPaid: true })}
                  className="w-4 h-4 accent-purple-600"
                />
                <div>
                  <span className="text-xs font-black text-slate-900 block">💳 Paid Entry Contest</span>
                  <span className="text-[11px] text-slate-500 font-medium">Participants pay a join price / entry fee to enter</span>
                </div>
              </label>
            </div>

            {/* Entry Fee Input Box (Rendered when isPaid is true) */}
            {formData.isPaid && (
              <div className="p-5 rounded-2xl bg-purple-50/60 border border-purple-200 space-y-3 animate-fade-in">
                <label className="block text-xs font-bold text-purple-900">
                  Join Price / Entry Fee Amount per Participant (₹) *
                </label>
                <div className="relative max-w-md">
                  <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-purple-700 font-bold text-sm">₹</span>
                  <input
                    type="number"
                    min="1"
                    required={formData.isPaid}
                    value={formData.entryFee}
                    onChange={(e) => setFormData({ ...formData, entryFee: e.target.value })}
                    placeholder="e.g. 50"
                    className="w-full bg-white border border-purple-300 rounded-xl pl-8 pr-4 py-3 text-sm text-purple-900 outline-none focus:border-purple-600 font-black"
                  />
                </div>
                <p className="text-[11px] text-slate-500 font-medium">
                  💡 Note: Participants will be required to pay ₹{formData.entryFee || 0} via Tapcoe App to enter this contest.
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Section 4: Dates & Timeline */}
        <div className="bg-white border border-slate-200 p-6 md:p-8 rounded-2xl space-y-6 shadow-sm">
          <div className="flex items-center gap-2 text-emerald-800 font-bold text-sm border-b border-slate-200 pb-3">
            <FiCalendar className="w-5 h-5 text-emerald-600" />
            <h2>4. Dates & Voting Timelines</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-xs font-bold text-slate-700 mb-1.5">Contest Start Date</label>
              <input
                type="date"
                value={formData.startDate}
                onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
                className="w-full bg-white border border-slate-300 rounded-xl p-3.5 text-xs text-slate-900 outline-none focus:border-purple-600 font-semibold"
              />
            </div>

            <div>
              <label className="block text-xs font-bold text-slate-700 mb-1.5">Voting Start Date</label>
              <input
                type="date"
                value={formData.votingStartDate}
                onChange={(e) => setFormData({ ...formData, votingStartDate: e.target.value })}
                className="w-full bg-white border border-slate-300 rounded-xl p-3.5 text-xs text-slate-900 outline-none focus:border-purple-600 font-semibold"
              />
            </div>

            <div>
              <label className="block text-xs font-bold text-slate-700 mb-1.5">Contest End / Expiry Date *</label>
              <input
                type="date"
                required
                value={formData.expiryDate}
                onChange={(e) => setFormData({ ...formData, expiryDate: e.target.value })}
                className="w-full bg-white border border-slate-300 rounded-xl p-3.5 text-xs text-slate-900 outline-none focus:border-purple-600 font-semibold"
              />
            </div>
          </div>
        </div>

        {/* Section 5: Prizes & Rewards */}
        <div className="bg-white border border-slate-200 p-6 md:p-8 rounded-2xl space-y-6 shadow-sm">
          <div className="flex items-center gap-2 text-amber-800 font-bold text-sm border-b border-slate-200 pb-3">
            <FiDollarSign className="w-5 h-5 text-amber-600" />
            <h2>5. Prizes & Rewards</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-bold text-slate-700 mb-1.5">Prize Summary</label>
              <input
                type="text"
                value={formData.prizeonewords}
                onChange={(e) => setFormData({ ...formData, prizeonewords: e.target.value })}
                placeholder="e.g. ₹25,000 Cash Prize + Trophy"
                className="w-full bg-white border border-slate-300 rounded-xl p-3.5 text-xs text-slate-900 outline-none focus:border-purple-600 font-medium"
              />
            </div>

            <div>
              <label className="block text-xs font-bold text-slate-700 mb-1.5">Cash Prize Amount (Numeric)</label>
              <input
                type="number"
                value={formData.cashPrize}
                onChange={(e) => setFormData({ ...formData, cashPrize: e.target.value })}
                placeholder="10000"
                className="w-full bg-white border border-slate-300 rounded-xl p-3.5 text-xs text-slate-900 outline-none focus:border-purple-600 font-bold"
              />
            </div>
          </div>
        </div>

        {/* Section 6: Rules & Description */}
        <div className="bg-white border border-slate-200 p-6 md:p-8 rounded-2xl space-y-6 shadow-sm">
          <div className="flex items-center gap-2 text-slate-900 font-bold text-sm border-b border-slate-200 pb-3">
            <FiFileText className="w-5 h-5 text-purple-700" />
            <h2>6. Rules & Description</h2>
          </div>

          <div>
            <label className="block text-xs font-bold text-slate-700 mb-1.5">Contest Rules & Submission Guidelines</label>
            <textarea
              rows={5}
              value={formData.about}
              onChange={(e) => setFormData({ ...formData, about: e.target.value, rules: e.target.value })}
              placeholder="Explain how participants can enter, photo/video requirements, voting rules and winner declaration process..."
              className="w-full bg-white border border-slate-300 rounded-xl p-3.5 text-xs text-slate-900 outline-none focus:border-purple-600 font-medium"
            />
          </div>
        </div>

        {/* Section 7: Feature Toggles */}
        <div className="bg-white border border-slate-200 p-6 md:p-8 rounded-2xl space-y-6 shadow-sm">
          <div className="flex items-center gap-2 text-slate-900 font-bold text-sm border-b border-slate-200 pb-3">
            <FiSliders className="w-5 h-5 text-purple-700" />
            <h2>7. Advanced Feature Toggles & Controls</h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="p-4 rounded-xl bg-slate-50 border border-slate-200 flex items-center justify-between">
              <div>
                <span className="text-xs font-bold text-slate-900 block">isFrameAllow</span>
                <span className="text-[10px] text-slate-500 font-medium">Allow photo frames</span>
              </div>
              <input
                type="checkbox"
                checked={formData.isFrameAllow}
                onChange={(e) => setFormData({ ...formData, isFrameAllow: e.target.checked })}
                className="w-5 h-5 accent-purple-700 rounded cursor-pointer"
              />
            </div>

            <div className="p-4 rounded-xl bg-slate-50 border border-slate-200 flex items-center justify-between">
              <div>
                <span className="text-xs font-bold text-slate-900 block">isVotingAllow</span>
                <span className="text-[10px] text-slate-500 font-medium">Enable public voting</span>
              </div>
              <input
                type="checkbox"
                checked={formData.isVotingAllow}
                onChange={(e) => setFormData({ ...formData, isVotingAllow: e.target.checked })}
                className="w-5 h-5 accent-purple-700 rounded cursor-pointer"
              />
            </div>

            <div className="p-4 rounded-xl bg-slate-50 border border-slate-200 flex items-center justify-between">
              <div>
                <span className="text-xs font-bold text-slate-900 block">isUploadAllow</span>
                <span className="text-[10px] text-slate-500 font-medium">Allow user uploads</span>
              </div>
              <input
                type="checkbox"
                checked={formData.isUploadAllow}
                onChange={(e) => setFormData({ ...formData, isUploadAllow: e.target.checked })}
                className="w-5 h-5 accent-purple-700 rounded cursor-pointer"
              />
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex justify-end gap-3 pt-4 border-t border-slate-200">
          <button
            type="button"
            onClick={() => history.goBack()}
            className="px-6 py-3 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-700 border border-slate-200 text-xs font-bold transition-colors"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={loading}
            className="px-8 py-3 rounded-xl bg-purple-700 hover:bg-purple-800 text-white text-xs font-bold transition-all shadow-sm flex items-center gap-2"
          >
            <FiCheck className="w-4 h-4" />
            <span>{loading ? 'Publishing...' : 'Publish Contest'}</span>
          </button>
        </div>
      </form>
    </div>
  );
};

export default CreateContest;
